import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PersonIcon from "@mui/icons-material/Person";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import BASEURL from "../../../../../../pages/Utils/Baseurl";
import { getManagerHierarchyQuery } from "./Queries";

const Hierarchy = ({ hierarchyOpen, setHierarchyOpen, sessionDetails }) => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hierarchyOpen && sessionDetails?.id) {
      fetchManagerHierarchy();
    }
  }, [hierarchyOpen, sessionDetails?.id]);

  const fetchManagerHierarchy = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        BASEURL,
        {
          query: getManagerHierarchyQuery,
          variables: { userId: String(sessionDetails?.id) },
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const result = response?.data?.data?.getManagerHierarchy;
      if (result?.success) {
        // Sort managers in descending order so highest level is at top, down to level 1 (direct manager) above user
        const sortedData = [...(result?.data || [])].sort(
          (a, b) => Number(b.level || 0) - Number(a.level || 0)
        );
        setManagers(sortedData);
      } else {
        setManagers([]);
        if (result?.message) {
          setError(result.message);
        }
      }
    } catch (err) {
      console.error("Error fetching manager hierarchy:", err);
      setError("Failed to fetch manager hierarchy.");
      setManagers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setHierarchyOpen(false);
  };

  const currentUserName = sessionDetails?.name || "You";
  const currentUserEmpId = sessionDetails?.empid || "";

  return (
    <Dialog
      open={Boolean(hierarchyOpen)}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #3f4277 0%, #3f4277 60%, #a1bfa3 100%)",
          px: 2.5,
          py: 2,
          color: "#fff",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(4px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <AccountTreeOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
                Manager Hierarchy
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                Reporting structure
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3, backgroundColor: "#f8f9fc" }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
            <CircularProgress size={36} sx={{ color: "#3f4277" }} />
            <Typography variant="body2" color="text.secondary">
              Loading hierarchy...
            </Typography>
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "14px",
              border: "1px solid #e8e8ee",
              backgroundColor: "#fff",
            }}
          >
            {/* No managers found notice */}
            {managers.length === 0 && !loading && (
              <Box
                sx={{
                  mb: 3,
                  p: 1.8,
                  borderRadius: "10px",
                  backgroundColor: "#fef6e7",
                  border: "1px solid #f9d49b",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#d97706", fontSize: 22 }} />
                <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 500 }}>
                  {error || "You don't have any hierarchy above you."}
                </Typography>
              </Box>
            )}

            {/* Metro Line UI */}
            <Box sx={{ position: "relative", pl: 1 }}>
              <style>
                {`
                  @keyframes metroPulse {
                    0% {
                      transform: scale(0.95);
                      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
                    }
                    70% {
                      transform: scale(1.15);
                      box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
                    }
                    100% {
                      transform: scale(0.95);
                      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
                    }
                  }
                  @keyframes managerPulse {
                    0% {
                      transform: scale(0.95);
                      box-shadow: 0 0 0 0 rgba(63, 66, 119, 0.7);
                    }
                    70% {
                      transform: scale(1.1);
                      box-shadow: 0 0 0 8px rgba(63, 66, 119, 0);
                    }
                    100% {
                      transform: scale(0.95);
                      box-shadow: 0 0 0 0 rgba(63, 66, 119, 0);
                    }
                  }
                `}
              </style>

              {/* Managers list nodes */}
              {managers.map((mgr, index) => {
                const isFirst = index === 0;   

                return (
                  <Box
                    key={mgr.id || index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      position: "relative",
                      pb: 3,
                    }}
                  >
                    {/* Metro Line connector */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 17,
                        top: 28,
                        bottom: 0,
                        width: "3px",
                        backgroundColor: "#c5c8dc",
                        borderRadius: "2px",
                      }}
                    />

                    {/* Metro Station Dot */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        border: "3px solid #3f4277",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                        mr: 2.5,
                        flexShrink: 0,
                        boxShadow: "0 2px 6px rgba(63, 66, 119, 0.25)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: "#3f4277",
                          animation: "managerPulse 2s infinite",
                        }}
                      />
                    </Box>

                    {/* Manager Card Info */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: "10px",
                        backgroundColor: "#f4f5fa",
                        border: "1px solid #e2e4f0",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#3f4277",
                          backgroundColor: "#edf0f8",
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ color: "#2f3152", fontSize: "14px" }}
                        >
                          {mgr.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.3,
                            borderRadius: "12px",
                            backgroundColor: "#e0e3f5",
                            color: "#3f4277",
                            fontWeight: 600,
                            fontSize: "11px",
                          }}
                        >
                          {mgr.level ? `Level ${mgr.level}` : `Manager ${index + 1}`}
                        </Typography>
                      </Stack>
                      {mgr.empid && (
                        <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.2, display: "block" }}>
                          Emp ID: {mgr.empid}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {/* Current User Node (Always the last station / your name) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                {/* Active Station Blinking Dot (Green Pulse) */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    border: "3px solid #2e7d32",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    mr: 2.5,
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(46, 125, 50, 0.3)",
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      backgroundColor: "#4caf50",
                      animation: "metroPulse 1.8s infinite",
                    }}
                  />
                </Box>

                {/* User Card Info */}
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#f0f9f1",
                    border: "1.5px solid #a5d6a7",
                    boxShadow: "0 2px 8px rgba(46, 125, 50, 0.08)",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <PersonIcon sx={{ fontSize: 18, color: "#2e7d32" }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: "#1b5e20", fontSize: "14px" }}
                      >
                        {currentUserName}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: "12px",
                        backgroundColor: "#c8e6c9",
                        color: "#1b5e20",
                        fontWeight: 700,
                        fontSize: "11px",
                      }}
                    >
                      You
                    </Typography>
                  </Stack>
                  {currentUserEmpId && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#2e7d32", mt: 0.2, display: "block", pl: 3.1 }}
                    >
                      Emp ID: {currentUserEmpId}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

Hierarchy.propTypes = {
  hierarchyOpen: PropTypes.bool,
  setHierarchyOpen: PropTypes.func,
  sessionDetails: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    empid: PropTypes.string,
  }),
};

export default Hierarchy;